import { styled } from '@mui/material/styles';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const deepTeal = '#047857';

/* ─── Page shell ─── */
export const SignUpPanel = styled('section')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 5fr) minmax(0, 6fr)',
  alignItems: 'start',
  gap: 40,
  margin: '12px 0 48px',
  padding: '44px 48px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 960px)': {
    gridTemplateColumns: '1fr',
    gap: 24,
    padding: '32px 28px',
  },
  '@media (max-width: 600px)': {
    margin: '8px 0 32px',
    padding: '22px 12px',
    borderRadius: 18,
  },
});

/* ─── Intro column ─── */
export const Intro = styled('div')({
  paddingTop: 8,
  '@media (max-width: 960px)': {
    paddingTop: 0,
  },
});

export const Eyebrow = styled('p')({
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
});

export const Heading = styled('h1')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '2.2rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 10px',
  '@media (max-width: 600px)': {
    fontSize: '1.65rem',
  },
});

export const Lead = styled('p')({
  fontSize: '1.05rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 0 20px',
  maxWidth: 440,
});

export const BenefitsList = styled('ul')({
  listStyle: 'none',
  padding: 0,
  margin: '0 0 24px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  '& li': {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: '0.98rem',
    fontWeight: 600,
    color: ink,
  },
  '& svg': {
    flex: '0 0 auto',
    fontSize: 22,
    padding: 3,
    borderRadius: '50%',
    color: deepTeal,
    background: 'rgba(1, 235, 178, 0.18)',
  },
  '@media (max-width: 960px)': {
    marginBottom: 16,
  },
});

export const IntroLinks = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: '0.95rem',
  color: muted,
  '& p': { margin: 0 },
  '& a': {
    fontWeight: 600,
    color: blue,
    textDecoration: 'none',
  },
  '& a:hover, & a:focus-visible': {
    textDecoration: 'underline',
    textUnderlineOffset: 3,
  },
});

/* ─── Form card ─── */
export const FormCard = styled('div')({
  padding: '28px 28px 24px',
  borderRadius: 20,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 8px 28px rgba(26, 26, 46, 0.08)',
  '@media (max-width: 600px)': {
    padding: '20px 16px 18px',
    borderRadius: 16,
  },
});

export const FormTitle = styled('h2')({
  fontFamily: "'Plus Jakarta Sans', sans-serif",
  fontSize: '1.25rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 18px',
});

export const FormContent = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

export const FormDivider = styled('hr')({
  margin: '2px 0',
  border: 0,
  borderTop: '1px solid rgba(26, 26, 46, 0.08)',
});

export const FormRow = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: 14,
  width: '100%',
  '@media (max-width: 600px)': {
    gridTemplateColumns: '1fr',
    gap: 12,
  },

  /* MUI TextField in brand form style */
  '& .MuiOutlinedInput-root': {
    borderRadius: 10,
    background: '#f7fafc',
    transition: 'background 0.15s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(26, 26, 46, 0.12)',
      borderWidth: 1.5,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(26, 26, 46, 0.24)',
    },
    '&.Mui-focused': {
      background: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: blue,
        borderWidth: 1.5,
      },
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#d32f2f',
    },
  },
  '& .MuiInputLabel-root': {
    color: muted,
    '&.Mui-focused': { color: blue },
    '&.Mui-error': { color: '#d32f2f' },
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 4,
    fontSize: '0.8rem',
  },
  '& .MuiCheckbox-root.Mui-checked': {
    color: blue,
  },
  '& .MuiFormControlLabel-label': {
    fontSize: '0.92rem',
    color: muted,
    '& a': {
      fontWeight: 600,
      color: blue,
      textDecoration: 'none',
    },
    '& a:hover': {
      textDecoration: 'underline',
    },
  },
});

/** Spans both columns of a FormRow (password fields). */
export const FullWidthField = styled('div')({
  gridColumn: '1 / -1',
});

export const SubmitButton = styled('button')({
  width: '100%',
  marginTop: 18,
  padding: '12px 24px',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '1rem',
  fontWeight: 700,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  transition: 'filter 0.15s ease, box-shadow 0.15s ease',
  '&:hover:not(:disabled)': {
    filter: 'brightness(1.05)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
  '&:focus-visible': {
    outline: '3px solid rgba(13, 78, 254, 0.35)',
    outlineOffset: 2,
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },
});

/* ─── Password strength ─── */
export const PasswordStrength = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  marginTop: 8,
});

export const PasswordStrengthBar = styled('div', {
  shouldForwardProp: (prop) => !['$score', '$maxScore', '$color'].includes(prop),
})(({ $score, $maxScore, $color }) => ({
  flex: 1,
  height: 4,
  borderRadius: 2,
  background: '#e0e4ec',
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    borderRadius: 2,
    background: $color || '#e0e4ec',
    width: `${($score / ($maxScore || 1)) * 100}%`,
    transition: 'width 0.35s ease, background 0.35s ease',
  },
}));

export const PasswordStrengthLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== '$color',
})(({ $color }) => ({
  minWidth: 70,
  fontSize: '0.78rem',
  fontWeight: 600,
  color: $color || muted,
}));

export const ConfirmPasswordMatch = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginTop: 8,
  fontSize: '0.82rem',
  fontWeight: 600,
  color: deepTeal,
});

export const ErrorMessage = styled('div')({
  marginTop: 16,
  padding: '12px 14px',
  borderRadius: 10,
  fontSize: '0.92rem',
  lineHeight: 1.45,
  color: '#b71c1c',
  background: 'rgba(211, 47, 47, 0.08)',
  border: '1px solid rgba(211, 47, 47, 0.3)',
});
